import React, { useCallback, useContext, useEffect, useState } from "react";
import { useVelavuToast } from "./velavu-toast";
import { ToastNotificationNoInternet } from "./toast-notification-builtins";

export type Refreshable = () => Promise<unknown>;

export interface RefreshContextProps {
	isRefreshing: boolean;
	refresh: () => void;
	registerRefreshable: (refreshable: Refreshable) => void;
	unregisterRefreshable: (refreshable: Refreshable) => void;
}

const RefreshContext = React.createContext<RefreshContextProps>({
	get isRefreshing(): never {
		throw new Error("No refresh context available");
	},
	refresh() {
		throw new Error("No refresh context available");
	},
	registerRefreshable() {
		throw new Error("No refresh context available");
	},
	unregisterRefreshable() {
		throw new Error("No refresh context available");
	},
});

export function RefreshContextProvider(props: { children?: React.ReactNode }) {
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [refreshables, setRefreshables] = useState<Refreshable[]>([]);
	const { pushToast } = useVelavuToast();

	const registerRefreshable = useCallback(
		(refreshable: Refreshable) => {
			setRefreshables((refreshables) => {
				return [...refreshables, refreshable];
			});
		},
		[setRefreshables],
	);

	const unregisterRefreshable = useCallback(
		(refreshable: Refreshable) => {
			setRefreshables((refreshables) => {
				return refreshables.filter((r) => r !== refreshable);
			});
		},
		[setRefreshables],
	);

	const refresh = useCallback(() => {
		if (isRefreshing) {
			return;
		}

		//Set the refreshing state
		setIsRefreshing(true);

		//Make sure that we don't push the error toast multiple times
		let errorToastPushed = false;

		//Invoke all refreshables
		const promises = refreshables.map((fn) => {
			return fn().catch((err) => {
				//Emit a toast message
				if (!errorToastPushed) {
					pushToast((close) => (
						<ToastNotificationNoInternet onDismiss={close} />
					));

					errorToastPushed = true;
				}
				return Promise.reject(err);
			});
		});

		//Reset the refreshing state once all refreshables are done
		Promise.allSettled(promises).then(() => {
			setIsRefreshing(false);
		});
	}, [refreshables, isRefreshing, setIsRefreshing, pushToast]);

	return (
		<RefreshContext.Provider
			value={{
				isRefreshing,
				refresh,
				registerRefreshable,
				unregisterRefreshable,
			}}
		>
			{props.children}
		</RefreshContext.Provider>
	);
}

/// Returns a function that can be used to invoke a refresh
export function useContentRefresh() {
	return useContext(RefreshContext);
}

/// Registers a refreshable function with the refresh context
export function useProvideContentRefreshable(refreshable: Refreshable) {
	const { registerRefreshable, unregisterRefreshable } =
		useContext(RefreshContext);

	useEffect(() => {
		registerRefreshable(refreshable);
		return () => {
			unregisterRefreshable(refreshable);
		};
	}, [refreshable, registerRefreshable, unregisterRefreshable]);
}
